import { HStack } from "@/components/layout/HStack";
import { VStack } from "@/components/layout/VStack";
import { Button } from "@/components/ui/Button/Button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/Command/Command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/Popover/Popover";
import { SelectStyles } from "@/components/ui/Select/Select";
import { normalize } from "@/utils/normalize";
import { Check, ChevronDownIcon } from "lucide-react";
import { useMemo, useState } from "react";

const ExplorationSearchSelect = ({
  explorations,
  teachers,
  value,
  onChange,
  disabled,
  placeholder = "Select an exploration",
  disabledIds,
}: {
  explorations: any[];
  teachers: any[] | null;
  value: string | null;
  onChange: (nextId: string) => void;
  disabled?: boolean;
  placeholder?: string;
  disabledIds?: Set<string>;
}) => {
  const [open, setOpen] = useState<boolean>(false);

  const selected = useMemo(
    () => explorations.find((e) => e._id === value) ?? null,
    [explorations, value],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={SelectStyles.trigger}
          {...(!value && {
            "data-placeholder": "",
          })}
          style={{ overflow: "hidden" }}
        >
          {selected
            ? `${selected.name} (${selected.enrolled}/${selected.maxEnrollment})`
            : placeholder}
          <ChevronDownIcon className={SelectStyles.triggerIcon} />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        style={{
          width: "var(--radix-popover-trigger-width)",
          padding: 0,
        }}
        align="end"
      >
        <Command
          filter={(itemValue, search) => {
            const value = normalize(itemValue);
            const query = normalize(search);
            if (!query) return 1;
            return value.includes(query) ? 1 : 0;
          }}
        >
          <CommandInput placeholder="Search explorations..." />
          <CommandList>
            <CommandEmpty>No explorations found.</CommandEmpty>

            <CommandGroup>
              {explorations.map((e) => {
                const isTaken = disabledIds?.has(e._id) ?? false;
                const isDisabled = isTaken && e._id !== value;

                return (
                  <CommandItem
                    key={e._id}
                    value={e.name}
                    disabled={isDisabled}
                    onSelect={() => {
                      onChange(e._id);
                      setOpen(false);
                    }}
                  >
                    <HStack
                      align="center"
                      justify="space-between"
                      style={{ width: "100%" }}
                    >
                      <VStack gap="0.25rem">
                        <span>{e.name}</span>
                        <span
                          style={{
                            color: "var(--muted-foreground)",
                            fontSize: "var(--text-xs)",
                            lineHeight: "var(--line-height-xs)",
                          }}
                        >
                          in {e.room} for grade{e.gradeLevels.length > 1 && "s"}{" "}
                          {e.gradeLevels.join(", ")}
                        </span>
                        {teachers && (
                          <span
                            style={{
                              color: "var(--muted-foreground)",
                              fontSize: "var(--text-xs)",
                              lineHeight: "var(--line-height-xs)",
                            }}
                          >
                            taught by{" "}
                            {teachers.find((t) => t._id === e.teacher).name}
                          </span>
                        )}
                        <span
                          style={{
                            color: "var(--muted-foreground)",
                            fontSize: "var(--text-xs)",
                            lineHeight: "var(--line-height-xs)",
                          }}
                        >
                          ({e.enrolled}/{e.maxEnrollment})
                        </span>
                      </VStack>
                      {value === e._id && (
                        <Check
                          style={{
                            width: "1rem",
                            height: "1rem",
                            color: "var(--secondary-foreground)",
                          }}
                        />
                      )}
                    </HStack>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export { ExplorationSearchSelect };
